import React, { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import { fermerDepotMobile, ouvrirDepotMobile, suivreDepotMobile } from './api';

const minutesSecondes = (s) =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/**
 * Le QR affiché sur l'ordinateur, avec son décompte.
 *
 * Le compte à rebours n'est pas décoratif : un QR reste affiché à l'écran et
 * peut être photographié par quelqu'un d'autre. Savoir qu'il se ferme seul
 * change la façon dont on le laisse traîner.
 */
export default function DepotMobile({ ouvert, onFermer, expertiseId,
                                      categorie, dommage, contenu, onDepot }) {
  const [depot, setDepot] = useState(null);
  const [minutes, setMinutes] = useState(15);
  const [restant, setRestant] = useState(0);
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState(false);
  const derniereCount = useRef(0);

  const ouvrir = useCallback(async (duree) => {
    setOccupe(true);
    setErreur('');
    try {
      const d = await ouvrirDepotMobile(expertiseId,
                                        { minutes: duree, categorie, dommage,
                                          contenu });
      setDepot(d);
      setRestant(d.secondes_restantes);
      derniereCount.current = d.pieces_deposees;
    } catch {
      setErreur("Impossible d'ouvrir le dépôt.");
    } finally {
      setOccupe(false);
    }
  }, [expertiseId, categorie, dommage, contenu]);

  useEffect(() => {
    if (ouvert && !depot) ouvrir(minutes);
  }, [ouvert, depot, minutes, ouvrir]);

  // Décompte local : une seconde d'horloge, pas un appel réseau.
  useEffect(() => {
    if (!ouvert || restant <= 0) return undefined;
    const t = setInterval(() => setRestant((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [ouvert, restant]);

  // Et un sondage plus lent pour voir arriver les photos.
  useEffect(() => {
    if (!ouvert || !depot?.jeton || restant <= 0) return undefined;
    const t = setInterval(async () => {
      try {
        const etat = await suivreDepotMobile(depot.jeton);
        setDepot((d) => ({ ...d, ...etat }));
        setRestant(etat.secondes_restantes);
        if (etat.pieces_deposees > derniereCount.current) {
          derniereCount.current = etat.pieces_deposees;
          onDepot?.();
        }
      } catch { /* le suivi peut échouer sans conséquence */ }
    }, 3000);
    return () => clearInterval(t);
  }, [ouvert, depot?.jeton, restant, onDepot]);

  const fermer = async () => {
    if (depot?.jeton) {
      try { await fermerDepotMobile(depot.jeton); } catch { /* déjà fermé */ }
    }
    setDepot(null);
    setRestant(0);
    onFermer();
  };

  const expire = restant <= 0;

  return (
    <Dialog open={ouvert} onClose={fermer} fullWidth maxWidth="xs">
      <DialogTitle>Photographier avec votre téléphone</DialogTitle>
      <DialogContent>
        <Stack spacing={2} alignItems="center">
          {erreur && <Alert severity="error" sx={{ width: '100%' }}>{erreur}</Alert>}

          {depot?.avertissement && (
            <Alert severity="warning" sx={{ width: '100%' }}>
              {depot.avertissement}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary">
            Scannez ce code avec l'appareil photo de votre téléphone. Il ouvrira
            une page où prendre vos photos ; elles arriveront ici toutes seules.
          </Typography>

          {!depot && !erreur && <CircularProgress size={28} />}

          {depot && (
            <>
              <Box
                sx={{ width: 240, height: 240,
                      filter: (expire || depot.avertissement)
                        ? 'grayscale(1) opacity(0.3)' : 'none',
                      '& svg': { width: '100%', height: '100%' } }}
                dangerouslySetInnerHTML={{ __html: depot.qr_svg }}
              />

              {expire ? (
                <Alert severity="warning" sx={{ width: '100%' }}>
                  Ce code a expiré. Ouvrez-en un nouveau.
                </Alert>
              ) : (
                <Box sx={{ width: '100%' }}>
                  <Stack direction="row" justifyContent="space-between"
                         sx={{ mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Se ferme dans
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {minutesSecondes(restant)}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (restant / (minutes * 60)) * 100)}
                  />
                </Box>
              )}

              {/* Le lien en clair : quand le scan échoue, on le tape ou on
                  le teste depuis le PC pour savoir si c'est le réseau. */}
              <Typography variant="caption" color="text.secondary"
                          sx={{ wordBreak: 'break-all', textAlign: 'center' }}>
                {depot.url}
              </Typography>

              <Typography variant="body2">
                {depot.pieces_deposees === 0
                  ? 'Aucune photo reçue pour le moment.'
                  : `${depot.pieces_deposees} photo${depot.pieces_deposees > 1 ? 's' : ''} reçue${depot.pieces_deposees > 1 ? 's' : ''}.`}
              </Typography>

              <ToggleButtonGroup
                exclusive size="small" value={minutes}
                onChange={(e, v) => { if (v) { setMinutes(v); ouvrir(v); } }}
                disabled={occupe}
              >
                <ToggleButton value={15} sx={{ textTransform: 'none' }}>
                  15 minutes
                </ToggleButton>
                <ToggleButton value={60} sx={{ textTransform: 'none' }}>
                  1 heure
                </ToggleButton>
              </ToggleButtonGroup>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={fermer} variant="contained">
          J'ai fini
        </Button>
      </DialogActions>
    </Dialog>
  );
}
